/* RESPONSIBLE TEAM: team-self-serve */

import type { CustomerCampaign } from 'embercom/lib/billing/campaign';
import type IntlService from 'ember-intl/services/intl';
import { BILLING_PERIODS } from 'embercom/lib/billing';
import moment from 'moment-timezone';

type CampaignLookupKey = 'campaign_lennys_podcast' | 'campaign_trial_reengagement';
export type CampaignTranslationKeys = {
  trialBannerText: string;
  trialBannerCtaLabel: string;
  checkoutsBannerText: string;
  successContentDescription: string;
  upgradeModalBannerText: string;
  welcomeScreenDescription: string;
  basketDescription: string;
  finMetricCardDescription: string;
  claimModalBadge: string;
  claimModalHeadline: string;
  claimModalBody: string;
  claimModalClaimCta: string;
  claimModalSkipCta: string;
};

export const CAMPAIGN_TRANSLATION_KEYS: Record<CampaignLookupKey, CampaignTranslationKeys> = {
  campaign_lennys_podcast: {
    trialBannerText: 'components.banners.campaigns.campaign_lennys_podcast-text',
    trialBannerCtaLabel: 'components.banners.campaigns.campaign_lennys_podcast-cta-label',
    checkoutsBannerText:
      'components.banners.campaigns.campaign_lennys_podcast-checkouts-banner-text',
    successContentDescription:
      'components.banners.campaigns.campaign_lennys_podcast-success-content-description',
    upgradeModalBannerText:
      'components.banners.campaigns.campaign_lennys_podcast-upgrade-modal-banner-text',
    welcomeScreenDescription:
      'components.banners.campaigns.campaign_lennys_podcast-welcome-screen-description',
    basketDescription:
      'signup.teams.pricing5.annual-plans.basket.lennys_podcast_campaign-basket-description',
    finMetricCardDescription:
      'signup.teams.pricing5.annual-plans.metrics.fin.lennys_podcast_campaign-description',
    claimModalBadge: '',
    claimModalHeadline: '',
    claimModalBody: '',
    claimModalClaimCta: '',
    claimModalSkipCta: '',
  },
  campaign_trial_reengagement: {
    trialBannerText: '',
    trialBannerCtaLabel: '',
    checkoutsBannerText: '',
    successContentDescription: '',
    upgradeModalBannerText: '',
    welcomeScreenDescription: '',
    basketDescription: '',
    finMetricCardDescription: '',
    claimModalBadge: 'components.compelling-offer.claim-modal.campaign_trial_reengagement.badge',
    claimModalHeadline:
      'components.compelling-offer.claim-modal.campaign_trial_reengagement.headline',
    claimModalBody: 'components.compelling-offer.claim-modal.campaign_trial_reengagement.body',
    claimModalClaimCta:
      'components.compelling-offer.claim-modal.campaign_trial_reengagement.claim_cta',
    claimModalSkipCta:
      'components.compelling-offer.claim-modal.campaign_trial_reengagement.skip_cta',
  },
};

export function findTranslationKey(
  campaign: CustomerCampaign | null | undefined,
  key: keyof CampaignTranslationKeys,
): string | undefined {
  let lookupKey = campaign?.lookup_key;

  if (!lookupKey || !(lookupKey in CAMPAIGN_TRANSLATION_KEYS)) {
    return undefined;
  }

  return CAMPAIGN_TRANSLATION_KEYS[lookupKey as CampaignLookupKey][key];
}

export function translateCampaignKey(
  intl: IntlService,
  {
    campaign,
    key,
    params,
  }: {
    campaign?: CustomerCampaign;
    key: keyof CampaignTranslationKeys;
    params?: Record<string, unknown>;
  },
): string {
  let translationKey = findTranslationKey(campaign, key);
  if (!translationKey) {
    return '';
  }

  return intl.t(translationKey, { htmlSafe: true, ...params }) as string;
}

interface CampaignDeps {
  intl: IntlService;
  redeemableCampaign: CustomerCampaign | undefined;
  ongoingCampaign: CustomerCampaign | undefined;
  cardlessTrialService: { daysLeftOfTrial: number };
}

export class CampaignTranslations {
  constructor(private deps: CampaignDeps) {}

  get checkoutsBannerText(): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'checkoutsBannerText',
      campaign: this.deps.redeemableCampaign,
    });
  }

  get trialBannerCtaLabel(): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'trialBannerCtaLabel',
      campaign: this.deps.redeemableCampaign,
    });
  }

  get trialBannerText(): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'trialBannerText',
      campaign: this.deps.redeemableCampaign,
      params: { trialEndInDays: this.deps.cardlessTrialService.daysLeftOfTrial },
    });
  }

  successContentDescription(planName?: string): string {
    return String(
      translateCampaignKey(this.deps.intl, {
        key: 'successContentDescription',
        params: { planName },
        campaign: this.deps.ongoingCampaign,
      }),
    );
  }

  welcomeScreenDescription(planName: string): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'welcomeScreenDescription',
      params: { planName },
      campaign: this.deps.redeemableCampaign,
    });
  }

  get upgradeModalBannerText(): string {
    let discountFinishesAt = this.deps.ongoingCampaign?.discount_finishes_at;
    let hasExpiry = discountFinishesAt ? 'true' : 'false';
    let date = discountFinishesAt ? moment(discountFinishesAt).format('D MMM, YYYY') : '';
    return translateCampaignKey(this.deps.intl, {
      key: 'upgradeModalBannerText',
      params: { hasExpiry, date },
      campaign: this.deps.ongoingCampaign,
    });
  }

  campaignBasketDescription(billingPeriod: BILLING_PERIODS): string | undefined {
    if (billingPeriod === BILLING_PERIODS.Annual) {
      return undefined;
    }
    return translateCampaignKey(this.deps.intl, {
      campaign: this.deps.redeemableCampaign,
      key: 'basketDescription',
    });
  }

  get finMetricCardDescriptionKey(): string | undefined {
    return findTranslationKey(this.deps.redeemableCampaign, 'finMetricCardDescription');
  }

  get claimModalBadge(): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'claimModalBadge',
      campaign: this.deps.redeemableCampaign,
    });
  }

  get claimModalHeadline(): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'claimModalHeadline',
      campaign: this.deps.redeemableCampaign,
    });
  }

  claimModalBody(expiryDate: string): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'claimModalBody',
      campaign: this.deps.redeemableCampaign,
      params: { hasExpiry: !!expiryDate, expiryDate },
    });
  }

  get claimModalClaimCta(): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'claimModalClaimCta',
      campaign: this.deps.redeemableCampaign,
    });
  }

  get claimModalSkipCta(): string {
    return translateCampaignKey(this.deps.intl, {
      key: 'claimModalSkipCta',
      campaign: this.deps.redeemableCampaign,
    });
  }
}
